import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import MemberBadge from "../components/MemberBadge";

/**
 * Session 57 · Admin → Members (unified directory).
 *
 * Filterable list of everyone in the system, showing the member classification
 * badge and quick-jump links to each user's timeline.
 */
const FILTERS = [
  { key: "founding_member", label: "Founding Members" },
  { key: "early_adopter", label: "Members" },
  { key: "admin", label: "Admins" },
  { key: "venue_owner", label: "Venue Owners" },
  { key: "pending_invite", label: "Pending Invitations" },
  { key: "activated", label: "Activated Accounts" },
];

export default function AdminMembers() {
  const { user } = useAuth();
  const [active, setActive] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null); // Session 58.2 · surface API errors instead of silently showing "No matches"
  const [q, setQ] = useState("");
  const [stats, setStats] = useState(null); // Session 58.2 · DB-truth totals independent of the members list
  // Session 58.3 · Bulk purge + per-row delete state.
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgePattern, setPurgePattern] = useState("example.com");
  const [purgePreview, setPurgePreview] = useState(null);
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [purgeErr, setPurgeErr] = useState("");
  const [rowDeleting, setRowDeleting] = useState(null); // user_id currently being deleted
  const [renameFor, setRenameFor] = useState(null); // {user_id, name} of member being renamed
  const [renameValue, setRenameValue] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);

  const runPurgePreview = async (pattern) => {
    setPurgeBusy(true); setPurgeErr("");
    try {
      const r = await api.post("/admin/members/purge-fakes", { pattern, dry_run: true });
      setPurgePreview(r.data);
    } catch (e) {
      setPurgeErr(e?.response?.data?.detail || e?.message || "Preview failed");
      setPurgePreview(null);
    } finally { setPurgeBusy(false); }
  };

  const confirmPurge = async () => {
    if (!purgePreview || purgePreview.matched === 0) return;
    setPurgeBusy(true); setPurgeErr("");
    try {
      const r = await api.post("/admin/members/purge-fakes", { pattern: purgePattern, dry_run: false });
      const n = r.data?.deleted || 0;
      setPurgeOpen(false);
      setPurgePreview(null);
      // reload the list + totals
      await load();
      api.get("/admin/founding-members/stats").then((rr) => setStats(rr.data || null)).catch(() => {});
      alert(`Deleted ${n} member${n === 1 ? "" : "s"}.`);
    } catch (e) {
      setPurgeErr(e?.response?.data?.detail || e?.message || "Delete failed");
    } finally { setPurgeBusy(false); }
  };

  const deleteOneMember = async (r) => {
    if (!window.confirm(`Delete ${r.email}? This cannot be undone.`)) return;
    setRowDeleting(r.user_id);
    try {
      await api.delete(`/admin/members/${r.user_id}`);
      setRows((cur) => cur.filter((x) => x.user_id !== r.user_id));
      // refresh totals
      api.get("/admin/founding-members/stats").then((rr) => setStats(rr.data || null)).catch(() => {});
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed");
    } finally {
      setRowDeleting(null);
    }
  };

  const openRename = (r) => {
    setRenameFor(r);
    setRenameValue(r.name || "");
  };

  const submitRename = async () => {
    if (!renameFor || !renameValue.trim() || renameValue.trim() === (renameFor.name || "").trim()) {
      setRenameFor(null);
      return;
    }
    setRenameBusy(true);
    try {
      const r = await api.patch(`/admin/members/${renameFor.user_id}/name`, { name: renameValue.trim() });
      const newName = r.data?.new_name || renameValue.trim();
      setRows((cur) => cur.map((x) => x.user_id === renameFor.user_id ? { ...x, name: newName } : x));
      setRenameFor(null);
    } catch (e) {
      alert(e?.response?.data?.detail || "Rename failed");
    } finally {
      setRenameBusy(false);
    }
  };

  const toggle = (key) => {
    setActive((cur) => cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]);
  };

  // Session 59.5 · P0 · Stable callback for exhaustive-deps.
  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const url = active.length ? `/admin/members?filter=${active.join(",")}` : "/admin/members";
      const r = await api.get(url);
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      // Session 58.2 · Prod was showing an empty page because the api call was
      // silently swallowed. Now we surface status + message so admins can
      // diagnose (auth cookie missing, 403 role mismatch, 502 upstream, etc.)
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      setErr({
        status: status || "network",
        message: detail || e?.message || "Request failed",
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [active]);

  useEffect(() => { load(); }, [load]);

  // Session 58.2 · Pull DB-truth totals from the stats endpoint so the admin
  // always sees the real member count even if the /admin/members list itself
  // errors. Separate axios call → separate failure surface.
  useEffect(() => {
    api.get("/admin/founding-members/stats")
      .then((r) => setStats(r.data || null))
      .catch(() => setStats(null));
  }, []);

  // Session 60.19 · State filter + alphabetization.
  const [stateFilter, setStateFilter] = useState("");
  const availableStates = useMemo(() => {
    const set = new Set();
    for (const r of rows) {
      const code = (r.state_code || "").trim().toUpperCase();
      if (code) set.add(code);
    }
    return Array.from(set).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    let arr = rows;
    if (stateFilter) {
      arr = arr.filter(r => (r.state_code || "").toUpperCase() === stateFilter);
    }
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      arr = arr.filter((r) => (r.email || "").toLowerCase().includes(needle)
        || (r.name || "").toLowerCase().includes(needle)
        || (r.city || "").toLowerCase().includes(needle)
        || (r.resolved_city || "").toLowerCase().includes(needle));
    }
    return [...arr].sort((a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || ""));
  }, [rows, q, stateFilter]);

  // Session 58.6 · Stripe environment pill (so admins instantly see whether
  // prod is in LIVE or TEST mode before running a smoke booking).
  const [stripeStatus, setStripeStatus] = useState(null);
  useEffect(() => {
    api.get("/admin/system/stripe-status")
      .then((r) => setStripeStatus(r.data || null))
      .catch(() => setStripeStatus(null));
  }, []);

  if (!user || user.role !== "admin") {
    return <div style={{ padding: 40 }}>Admin access required.</div>;
  }

  return (
    <div data-testid="admin-members-page" style={{ minHeight: "100vh", background: "#FAF8F3", padding: "40px 24px 80px", fontFamily: "Helvetica,Arial,sans-serif", color: "#1B2340" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 20 }}>
          <div>
            <div style={{ letterSpacing: "0.22em", color: "#B8913A", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", gap: 10 }}>
              <span>MÉTIER · ADMIN</span>
              {stripeStatus && (
                <span
                  data-testid={`stripe-mode-pill-${stripeStatus.mode}`}
                  title={
                    stripeStatus.mode === "live"
                      ? "PRODUCTION Stripe key is LIVE. Test cards will be REJECTED. Real charges only."
                      : stripeStatus.mode === "test"
                      ? "Stripe is in TEST mode. Test cards (4242…) work; no real money."
                      : "Stripe key prefix could not be determined."
                  }
                  style={{
                    padding: "2px 9px",
                    borderRadius: 999,
                    fontSize: 10,
                    letterSpacing: "0.08em",
                    fontWeight: 800,
                    border: "1px solid",
                    ...(stripeStatus.mode === "live"
                      ? { background: "rgba(122,27,34,0.09)", color: "#7A1B22", borderColor: "rgba(122,27,34,0.35)" }
                      : stripeStatus.mode === "test"
                      ? { background: "rgba(184,145,58,0.10)", color: "#8a6820", borderColor: "rgba(184,145,58,0.40)" }
                      : { background: "rgba(27,35,64,0.05)", color: "rgba(27,35,64,0.55)", borderColor: "rgba(27,35,64,0.20)" }),
                  }}
                >
                  STRIPE · {String(stripeStatus.mode).toUpperCase()}
                </span>
              )}
            </div>
            <h1 style={{ fontFamily: "Georgia,serif", fontWeight: 500, fontSize: 34, margin: "6px 0 0" }}>Members</h1>
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <Link to="/admin/founding-members" style={ghostLink}>Founding Members →</Link>
            <Link to="/admin/alpha" style={ghostLink}>Alpha Dashboard →</Link>
            <button
              data-testid="purge-fakes-btn"
              onClick={() => { setPurgeOpen(true); setPurgePreview(null); setPurgeErr(""); setPurgePattern("example.com"); }}
              style={{
                padding: "6px 12px", borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
                border: "1px solid rgba(196,60,60,0.35)", background: "#fff", color: "#7A1B22", cursor: "pointer",
              }}
            >Purge Test Accounts</button>
          </div>
        </div>

        {/* Session 58.2 · DB-truth totals strip. Always visible so admins can
            diff what the DB actually has vs what the list below is showing. */}
        {stats && (
          <div data-testid="members-totals-strip" style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
            {[
              { k: "Founding", v: stats.founding_members || 0, test: "founding" },
              { k: "Members", v: stats.early_adopters || 0, test: "early-adopters" },
              { k: "Admins", v: stats.admins || 0, test: "admins" },
              { k: "Venue Owners", v: stats.venue_owners || 0, test: "owners" },
              { k: "Pending", v: stats.pending || 0, test: "pending" },
              { k: "Activated", v: stats.activated || 0, test: "activated" },
            ].map((s) => (
              <div key={s.k} data-testid={`totals-${s.test}`} style={{
                background: "#fff",
                border: "1px solid rgba(27,35,64,0.10)",
                borderRadius: 10,
                padding: "10px 14px",
                minWidth: 90,
              }}>
                <div style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(27,35,64,0.55)", fontWeight: 600 }}>{s.k}</div>
                <div style={{ fontSize: 22, fontFamily: "Georgia,serif", fontWeight: 500, color: "#1B2340", marginTop: 2 }}>{s.v}</div>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => toggle(f.key)}
              style={{
                padding: "6px 14px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.04em",
                cursor: "pointer",
                border: active.includes(f.key) ? "1px solid #B8913A" : "1px solid rgba(27,35,64,0.16)",
                background: active.includes(f.key) ? "#B8913A" : "#fff",
                color: active.includes(f.key) ? "#fff" : "#1B2340",
              }}
            >
              {f.label}
            </button>
          ))}
          <input
            data-testid="member-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, city…"
            style={{ marginLeft: "auto", padding: "6px 12px", border: "1px solid rgba(27,35,64,0.16)", borderRadius: 999, fontSize: 12, minWidth: 220 }}
          />
          {/* Session 60.19 · State filter — alphabetized 2-letter codes
              from the members currently loaded (ZIP-derived state_code). */}
          <select
            data-testid="member-state-filter"
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            style={{ padding: "6px 10px", border: "1px solid rgba(27,35,64,0.16)", borderRadius: 999, fontSize: 12 }}
          >
            <option value="">All states</option>
            {availableStates.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid rgba(27,35,64,0.08)", overflow: "hidden" }}>
          <div style={{ padding: "14px 20px", borderBottom: "1px solid rgba(27,35,64,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <strong data-testid="members-count">{filtered.length} member{filtered.length === 1 ? "" : "s"}</strong>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <span style={{ fontSize: 12, color: "rgba(27,35,64,0.55)" }}>
                {active.length ? active.map((a) => FILTERS.find((f) => f.key === a)?.label).join(" · ") : "All"}
              </span>
              <button onClick={load} data-testid="members-refresh" style={{
                padding: "5px 12px", borderRadius: 999, fontSize: 11, fontWeight: 600, letterSpacing: "0.04em",
                border: "1px solid rgba(27,35,64,0.16)", background: "#fff", cursor: "pointer", color: "#1B2340",
              }}>Refresh</button>
            </div>
          </div>
          {err ? (
            <div data-testid="members-error" style={{ padding: 40, textAlign: "center" }}>
              <div style={{ display: "inline-block", padding: "16px 20px", background: "#FDF1EC", border: "1px solid #E5A0A0", borderRadius: 10, color: "#7A1B22", fontSize: 13, textAlign: "left", maxWidth: 520 }}>
                <div style={{ fontWeight: 700, marginBottom: 4, letterSpacing: "0.04em", textTransform: "uppercase", fontSize: 11 }}>
                  Failed to load members · HTTP {err.status}
                </div>
                <div style={{ fontFamily: "monospace", fontSize: 12, lineHeight: 1.5 }}>{err.message}</div>
                <div style={{ marginTop: 10, fontSize: 11, color: "rgba(27,35,64,0.6)" }}>
                  {err.status === 401 && "Your admin session may have expired — sign out and back in."}
                  {err.status === 403 && "Signed-in user isn't marked as admin on the API. Verify role in the DB."}
                  {err.status === 404 && "The /api/admin/members endpoint isn't mounted on this backend build. Redeploy required."}
                  {err.status === 500 && "Server-side crash — check backend logs for stack trace."}
                  {err.status === "network" && "Browser could not reach the API. Check REACT_APP_BACKEND_URL + CORS."}
                </div>
              </div>
            </div>
          ) : loading ? (
            <div style={{ padding: 40, textAlign: "center", color: "rgba(27,35,64,0.5)" }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div data-testid="members-empty" style={{ padding: 40, textAlign: "center", color: "rgba(27,35,64,0.5)" }}>
              {rows.length === 0
                ? "No members in this environment yet."
                : "No members match this filter + search combination."}
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#FAF8F3", textAlign: "left", color: "rgba(27,35,64,0.7)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  <th style={th}>Member</th>
                  <th style={th}>Type</th>
                  <th style={th}>City</th>
                  <th style={th}>Joined</th>
                  <th style={th}>Status</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.user_id} style={{ borderTop: "1px solid rgba(27,35,64,0.06)" }} data-testid={`member-row-${r.user_id}`}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{r.name || "—"}</div>
                      <div style={{ fontSize: 12, color: "rgba(27,35,64,0.55)" }}>{r.email}</div>
                    </td>
                    <td style={td}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                        <MemberBadge member_type={r.member_type} size="xs" />
                        {r.is_owner && <span style={pillOwner}>Venue Owner</span>}
                      </div>
                    </td>
                    <td style={td}>{r.city || "—"}</td>
                    <td style={td}>{fmt(r.created_at)}</td>
                    <td style={td}>
                      {r.must_change_password ? <span style={pillPending}>Pending</span>
                        : r.password_created_at || r.first_login_at ? <span style={pillActive}>Active</span>
                        : <span style={pillNew}>New</span>}
                    </td>
                    <td style={td}>
                      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <Link to={`/admin/members/${r.user_id}`} style={ghostLink} data-testid={`timeline-link-${r.user_id}`}>Timeline →</Link>
                        <button
                          data-testid={`rename-member-${r.user_id}`}
                          onClick={() => openRename(r)}
                          style={{
                            padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, letterSpacing: "0.04em",
                            border: "1px solid rgba(184,145,58,0.35)", background: "#fff", color: "#8a6820",
                            cursor: "pointer",
                          }}
                        >Rename</button>
                        <button
                          data-testid={`delete-member-${r.user_id}`}
                          onClick={() => deleteOneMember(r)}
                          disabled={rowDeleting === r.user_id}
                          style={{
                            padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, letterSpacing: "0.04em",
                            border: "1px solid rgba(196,60,60,0.35)", background: "#fff", color: "#7A1B22",
                            cursor: rowDeleting === r.user_id ? "not-allowed" : "pointer",
                            opacity: rowDeleting === r.user_id ? 0.5 : 1,
                          }}
                        >{rowDeleting === r.user_id ? "…" : "Delete"}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Session 58.7 · P1 · Rename member modal */}
      {renameFor && (
        <div
          data-testid="rename-modal"
          onClick={() => !renameBusy && setRenameFor(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(23,30,56,0.65)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20, zIndex: 100,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 14, width: "100%", maxWidth: 460,
              padding: "28px 30px 22px", boxShadow: "0 30px 80px -30px rgba(0,0,0,0.5)",
            }}
          >
            <div style={{ letterSpacing: "0.22em", color: "#B8913A", fontSize: 11, fontWeight: 700 }}>MEMBER · RENAME</div>
            <h2 style={{ fontFamily: "Georgia,serif", fontWeight: 500, fontSize: 24, margin: "6px 0 10px", color: "#1B2340" }}>
              Correct display name
            </h2>
            <p style={{ fontSize: 13, color: "rgba(27,35,64,0.65)", margin: "0 0 14px", lineHeight: 1.55 }}>
              Renaming <strong>{renameFor.email}</strong>. Only the display name + initials update — email, user_id, membership status, activity history and relationships are untouched.
            </p>
            <label style={{ display: "block", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(27,35,64,0.65)", fontWeight: 600, marginBottom: 4 }}>
              New display name
            </label>
            <input
              data-testid="rename-name-input"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitRename(); if (e.key === "Escape") setRenameFor(null); }}
              autoFocus
              style={{ width: "100%", padding: "9px 12px", border: "1px solid rgba(27,35,64,0.16)", borderRadius: 8, fontSize: 14, marginBottom: 16 }}
            />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                data-testid="rename-cancel"
                onClick={() => setRenameFor(null)}
                disabled={renameBusy}
                style={{ padding: "9px 18px", borderRadius: 999, fontSize: 12, fontWeight: 600, border: "1px solid rgba(27,35,64,0.16)", background: "#fff", color: "#1B2340", cursor: renameBusy ? "not-allowed" : "pointer" }}
              >Cancel</button>
              <button
                data-testid="rename-confirm"
                onClick={submitRename}
                disabled={renameBusy || !renameValue.trim() || renameValue.trim() === (renameFor.name || "").trim()}
                style={{ padding: "9px 18px", borderRadius: 999, fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", border: "1px solid #B8913A", background: "#B8913A", color: "#fff", cursor: renameBusy ? "not-allowed" : "pointer", opacity: (!renameValue.trim() || renameValue.trim() === (renameFor.name || "").trim()) ? 0.4 : 1 }}
              >{renameBusy ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Session 58.3 · Purge Test Accounts modal */}
      {purgeOpen && (
        <div
          data-testid="purge-modal"
          onClick={() => !purgeBusy && setPurgeOpen(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(23,30,56,0.65)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20, zIndex: 100,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 14, width: "100%", maxWidth: 560,
              padding: "28px 30px 24px", boxShadow: "0 30px 80px -30px rgba(0,0,0,0.5)",
            }}
          >
            <div style={{ letterSpacing: "0.22em", color: "#7A1B22", fontSize: 11, fontWeight: 700 }}>DANGER · BULK DELETE</div>
            <h2 style={{ fontFamily: "Georgia,serif", fontWeight: 500, fontSize: 24, margin: "6px 0 8px", color: "#1B2340" }}>
              Purge test accounts
            </h2>
            <p style={{ fontSize: 13, color: "rgba(27,35,64,0.7)", margin: "0 0 16px", lineHeight: 1.55 }}>
              Deletes every member whose email contains the pattern below.
              <br />
              <strong>Admins</strong> and <strong>@workmetier.com / @workmetier.app</strong> addresses are always protected. Related records (timeline, invites, notifications, team grants) are cascade-deleted. Action is logged to the audit log.
            </p>

            <label style={{ display: "block", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(27,35,64,0.65)", fontWeight: 600, marginBottom: 4 }}>
              Match pattern (email substring)
            </label>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <input
                data-testid="purge-pattern-input"
                value={purgePattern}
                onChange={(e) => { setPurgePattern(e.target.value); setPurgePreview(null); }}
                placeholder="e.g. example.com"
                style={{
                  flex: 1, padding: "8px 12px", border: "1px solid rgba(27,35,64,0.16)",
                  borderRadius: 8, fontSize: 13, fontFamily: "monospace",
                }}
              />
              <button
                data-testid="purge-preview-btn"
                onClick={() => runPurgePreview(purgePattern)}
                disabled={purgeBusy || !purgePattern.trim()}
                style={{
                  padding: "8px 16px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                  border: "1px solid rgba(27,35,64,0.18)", background: "#fff", color: "#1B2340",
                  cursor: purgeBusy ? "not-allowed" : "pointer",
                }}
              >{purgeBusy ? "…" : "Preview"}</button>
            </div>

            {purgeErr && (
              <div style={{ background: "#FDF1EC", border: "1px solid #E5A0A0", color: "#7A1B22", padding: "8px 12px", borderRadius: 8, fontSize: 12, marginBottom: 12 }} data-testid="purge-error">
                {purgeErr}
              </div>
            )}

            {purgePreview && (
              <div data-testid="purge-preview" style={{ background: "#FAF8F3", border: "1px solid rgba(27,35,64,0.10)", borderRadius: 8, padding: "12px 14px", marginBottom: 14, maxHeight: 260, overflowY: "auto" }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: "#1B2340" }}>
                  {purgePreview.matched} match{purgePreview.matched === 1 ? "" : "es"}
                </div>
                {purgePreview.matched === 0 ? (
                  <div style={{ fontSize: 12, color: "rgba(27,35,64,0.6)" }}>Nothing to delete for this pattern.</div>
                ) : (
                  <ul style={{ margin: 0, padding: 0, listStyle: "none", fontSize: 12, fontFamily: "monospace", color: "#1B2340" }}>
                    {purgePreview.users.map((u) => (
                      <li key={u.user_id} style={{ padding: "3px 0", borderBottom: "1px dashed rgba(27,35,64,0.08)" }} data-testid={`purge-preview-row-${u.user_id}`}>
                        <span>{u.email}</span> <span style={{ color: "rgba(27,35,64,0.5)" }}>· {u.name || "—"}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                data-testid="purge-cancel-btn"
                onClick={() => setPurgeOpen(false)}
                disabled={purgeBusy}
                style={{
                  padding: "9px 18px", borderRadius: 999, fontSize: 12, fontWeight: 600,
                  border: "1px solid rgba(27,35,64,0.16)", background: "#fff", color: "#1B2340",
                  cursor: purgeBusy ? "not-allowed" : "pointer",
                }}
              >Cancel</button>
              <button
                data-testid="purge-confirm-btn"
                onClick={confirmPurge}
                disabled={purgeBusy || !purgePreview || purgePreview.matched === 0}
                style={{
                  padding: "9px 18px", borderRadius: 999, fontSize: 12, fontWeight: 700, letterSpacing: "0.04em",
                  border: "1px solid #7A1B22", background: "#7A1B22", color: "#fff",
                  cursor: (purgeBusy || !purgePreview || purgePreview.matched === 0) ? "not-allowed" : "pointer",
                  opacity: (purgeBusy || !purgePreview || purgePreview.matched === 0) ? 0.4 : 1,
                }}
              >{purgeBusy ? "Deleting…" : `Delete ${purgePreview?.matched || 0}`}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return "—"; }
}
const ghostLink = { fontSize: 13, color: "#B8913A", textDecoration: "none", fontWeight: 600 };
const th = { padding: "10px 16px" };
const td = { padding: "12px 16px", verticalAlign: "top" };
const pillPending = { display: "inline-block", padding: "2px 8px", background: "#FFF3D6", color: "#7C5B10", fontSize: 11, borderRadius: 999, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" };
const pillActive = { display: "inline-block", padding: "2px 8px", background: "#DFEEE1", color: "#1B4634", fontSize: 11, borderRadius: 999, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" };
const pillNew = { display: "inline-block", padding: "2px 8px", background: "#E4EAF3", color: "#1B2340", fontSize: 11, borderRadius: 999, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" };
const pillOwner = { display: "inline-block", padding: "2px 8px", background: "#F3EEDF", color: "#8B6A22", fontSize: 10, borderRadius: 999, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" };
